import { db, type StoredImage } from '../db/schema';
import { forgetSizes } from '../db/images';
import { openZip, ZipFormatError, type ZipReader } from './unzip';
import { DATA, isManifest, MANIFEST, SCHEMA_VERSION, type Backup, type Manifest } from './format';
import { planRestore, type Mode, type Plan } from './merge';

/**
 * Reading a `.visilist` back in.
 *
 * Two steps, deliberately: `readBackup` opens the file and says what's in it,
 * and only `applyBackup` writes. The dialog in between is where merge or
 * replace gets chosen, and it can describe what it's about to do because it
 * already knows.
 */

export type Loaded = {
  manifest: Manifest;
  backup: Backup;
  zip: ZipReader;
};

export type Progress = {
  phase: 'checking' | 'restoring';
  done: number;
  total: number;
};

export type OnProgress = (progress: Progress) => void;

/** Photos are written in batches; one bulkPut of four hundred blobs is not. */
const BATCH = 20;

export async function readBackup(file: Blob): Promise<Loaded> {
  const zip = await openZip(file);

  const manifestText = await zip.text(MANIFEST);
  const dataText = await zip.text(DATA);
  if (manifestText === undefined || dataText === undefined) {
    throw new ZipFormatError('That file isn’t a VisiList backup — it has no manifest.');
  }

  const manifest: unknown = JSON.parse(manifestText);
  if (!isManifest(manifest)) {
    throw new ZipFormatError('That file isn’t a VisiList backup.');
  }

  // Forward compatibility only goes one way: this build can't be expected to
  // understand a format written by a later one.
  if (manifest.schemaVersion > SCHEMA_VERSION) {
    throw new ZipFormatError(
      'That backup was written by a newer version of VisiList. Update the app and try again.',
    );
  }

  const parsed = JSON.parse(dataText) as Backup;

  // An image the file lists but didn't pack is one the planner must treat as
  // absent, or an item comes back pointing at bytes that were never there.
  const backup: Backup = {
    ...parsed,
    images: parsed.images.filter((image) => zip.has(image.file) && zip.has(image.thumbFile)),
  };

  return { manifest, backup, zip };
}

export type Restored = {
  added: Plan['added'];
  /** Items whose room was in neither the file nor the database. */
  orphaned: number;
};

/**
 * Every byte in the file is checksummed before anything is written or deleted.
 * Replace empties the database first, and discovering a damaged photo halfway
 * through that is not a discovery worth making.
 */
export async function applyBackup(
  loaded: Loaded,
  mode: Mode,
  onProgress?: OnProgress,
): Promise<Restored> {
  await check(loaded.zip, onProgress);

  const plan = planRestore(loaded.backup, await snapshot(), mode);

  if (mode === 'replace') await clear();

  await writeImages(loaded, plan, onProgress);

  await db.transaction('rw', db.rooms, db.sections, db.items, db.imports, async () => {
    await db.rooms.bulkPut(plan.rooms);
    await db.sections.bulkPut(plan.sections);
    await db.items.bulkPut(plan.items);
    await db.imports.bulkPut(plan.imports);
  });

  return { added: plan.added, orphaned: plan.orphaned };
}

/** Reads the whole archive and throws on the first entry that doesn't sum. */
async function check(zip: ZipReader, onProgress?: OnProgress): Promise<void> {
  let done = 0;

  for (const name of zip.names) {
    await zip.blob(name);
    done += 1;
    onProgress?.({ phase: 'checking', done, total: zip.names.length });
  }
}

async function snapshot() {
  const [rooms, sections, items, imports, imageIds] = await Promise.all([
    db.rooms.toArray(),
    db.sections.toArray(),
    db.items.toArray(),
    db.imports.toArray(),
    db.images.toCollection().primaryKeys(),
  ]);

  return { rooms, sections, items, imports, imageIds };
}

async function clear(): Promise<void> {
  const gone = await db.images.toCollection().primaryKeys();

  await db.transaction('rw', db.rooms, db.sections, db.items, db.imports, db.images, async () => {
    await Promise.all([
      db.rooms.clear(),
      db.sections.clear(),
      db.items.clear(),
      db.imports.clear(),
      db.images.clear(),
    ]);
  });

  // The measurement cache would otherwise still answer for ids that are gone,
  // and a restore is very likely to bring the same ones back.
  forgetSizes(gone);
}

/**
 * Outside any transaction, in batches. A restore is hundreds of megabytes of
 * blobs; holding them all in one write would sit on the database for as long
 * as it took and time itself out on the way.
 */
async function writeImages(
  loaded: Loaded,
  plan: Plan,
  onProgress?: OnProgress,
): Promise<void> {
  let done = 0;
  let batch: StoredImage[] = [];

  for (const meta of plan.images) {
    const blob = await loaded.zip.blob(meta.file);
    const thumb = await loaded.zip.blob(meta.thumbFile);
    done += 1;
    onProgress?.({ phase: 'restoring', done, total: plan.images.length });

    // `readBackup` drops metadata whose bytes are missing, so this only fires
    // if the archive changed underneath us.
    if (blob === undefined || thumb === undefined) continue;

    batch.push({
      id: meta.id,
      blob,
      thumb,
      w: meta.w,
      h: meta.h,
      mime: meta.mime,
      addedAt: meta.addedAt,
    });

    if (batch.length >= BATCH) {
      await db.images.bulkPut(batch);
      batch = [];
    }
  }

  if (batch.length > 0) await db.images.bulkPut(batch);
}
